import { Photo } from '@features/visits/api';
import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';

export const useVisitDetailPhotoGallery = (
  onDelete: (photoId: string, photoUrl: string) => Promise<void>,
) => {
  const intl = useIntl();
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [deleteConfirmPhoto, setDeleteConfirmPhoto] = useState<Photo | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = useCallback(async () => {
    if (!deleteConfirmPhoto) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await onDelete(deleteConfirmPhoto.id, deleteConfirmPhoto.url);
      setDeleteConfirmPhoto(null);
      if (selectedPhoto?.id === deleteConfirmPhoto.id) {
        setSelectedPhoto(null);
      }
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError(
        intl.formatMessage({
          id: 'hairstyle.photo.delete.error',
          defaultMessage: 'Nepodařilo se smazat fotografii. Zkuste to prosím znovu.',
        }),
      );
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConfirmPhoto, selectedPhoto, onDelete, intl]);

  const handlePhotoClick = useCallback((photo: Photo) => {
    setSelectedPhoto(photo);
  }, []);

  const handleClosePhotoView = useCallback(() => {
    setSelectedPhoto(null);
  }, []);

  const handleDeleteClick = useCallback((photo: Photo) => {
    setDeleteConfirmPhoto(photo);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmPhoto(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    selectedPhoto,
    deleteConfirmPhoto,
    isDeleting,
    error,
    handleDelete,
    handlePhotoClick,
    handleClosePhotoView,
    handleDeleteClick,
    handleCancelDelete,
    clearError,
  };
};
